
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, ChevronRight, LogOut, Menu, Search, X } from "lucide-react";
import { useSession } from "@/features/auth";
import { Button, Skeleton } from "@tsi/ui";
import { Sidebar } from "./Sidebar";
import { NAV_ITEMS } from "./Navigation";
import { SearchModal } from "./SearchModal";
import { useZoom, ZoomControl } from "./ZoomControl";
import { NotificationsPopover } from "./NotificationsPopover";
import { useNotificacoesNaoLidas } from "@/features/notificacoes/notificacoes.api";
import { cn } from "@tsi/ui";
import { LinhaSkeleton } from "@/shared/components/LinhaSkeleton";

export function Header() {
  const { usuario, sair } = useSession();
  const iniciais = (usuario?.nome ?? "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [zoom, setZoom] = useZoom();
  const bellRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const current = NAV_ITEMS.find((n) => pathname.startsWith(n.href));

  // Atalhos de teclado: Cmd+K / Ctrl+K abre busca
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Notificações não lidas (as reais, da API) no selo do sino
  const totalUnread = useNotificacoesNaoLidas();

  return (
    <>
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-4 border-b border-border/70 bg-background/70 px-4 backdrop-blur-xl md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-4 w-4" />
          </Button>

          {/* Logo + breadcrumb */}
          <div className="flex items-center gap-2.5">
            <Link
              to="/estoque"
              aria-label="TSI início"
              className="flex h-8 w-[140px] shrink-0 items-center"
            >
              <img
                src="/brand/tsi-logo.png"
                alt="TSI"
                className="h-[1.925rem] w-auto max-w-full object-contain"
              />
            </Link>
            <div className="hidden items-center gap-2 border-l border-border/70 pl-3 text-[13px] sm:flex">
              <ChevronRight className="hidden h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium text-foreground">
                {current?.label ?? "Painel"}
              </span>
            </div>
          </div>
        </div>

        <div className="hidden flex-1 items-center justify-center px-4 lg:flex">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="group flex h-9 w-full max-w-[420px] items-center gap-2.5 rounded-lg border border-border/80 bg-card px-3 text-[13px] text-muted-foreground shadow-card transition-all hover:border-border hover:shadow-card-hover"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">
              Buscar cartas, reservas, parceiros…
            </span>
            <kbd className="hidden items-center gap-0.5 rounded-md border border-border/80 bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline-flex">
              ⌘K
            </kbd>
          </button>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Mobile search button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSearchOpen(true)}
            aria-label="Buscar"
          >
            <Search className="h-4 w-4" />
          </Button>

          <ZoomControl value={zoom} onChange={setZoom} />

          {/* Notificações com popover ancorado */}
          <div className="relative">
            <Button
              ref={bellRef}
              variant="ghost"
              size="icon"
              aria-label="Notificações"
              className="relative"
              onClick={() => setNotifOpen((v) => !v)}
            >
              <Bell className="h-4 w-4" />
              {totalUnread > 0 && (
                <span
                  className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[9px] font-bold text-white ring-2 ring-background tabular-nums"
                >
                  {totalUnread > 9 ? "9+" : totalUnread}
                </span>
              )}
            </Button>
            <NotificationsPopover
              open={notifOpen}
              onOpenChange={setNotifOpen}
              anchorRef={bellRef}
            />
          </div>

          {/* User */}
          <div className="ml-1 flex items-center gap-2 rounded-lg border border-border/70 bg-card py-1 pl-1 pr-2.5 shadow-card">
            {usuario ? (
              <>
                <div className="relative flex h-6 w-6 items-center justify-center overflow-hidden rounded-md bg-linear-to-br from-brand-600 via-brand to-brand-800 text-[10px] font-semibold text-white">
                  {iniciais || "?"}
                </div>
                <span className="hidden text-[13px] font-medium sm:inline">{usuario.nome}</span>
              </>
            ) : (
              // Só o nome do usuário é dado: em skeleton enquanto a sessão é conferida.
              <>
                <Skeleton className="h-6 w-6" />
                <span className="hidden text-[13px] sm:inline">
                  <LinhaSkeleton className="w-24" />
                </span>
              </>
            )}
            <button
              type="button"
              onClick={() => void sair()}
              title="Sair"
              aria-label="Sair"
              className="ml-1 text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* Search modal */}
      <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed inset-0 z-40 md:hidden",
          open ? "pointer-events-auto" : "pointer-events-none",
        )}
        aria-hidden={!open}
      >
        <div
          className={cn(
            "absolute inset-0 bg-ink-900/40 backdrop-blur-xs transition-opacity",
            open ? "opacity-100" : "opacity-0",
          )}
          onClick={() => setOpen(false)}
        />
        <aside
          className={cn(
            "absolute left-0 top-0 h-full w-[260px] border-r border-border bg-background shadow-soft transition-transform",
            open ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="flex items-center justify-between p-3">
            <span className="text-sm font-semibold">Menu</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(false)}
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <Sidebar onNavigate={() => setOpen(false)} embedded />
        </aside>
      </div>
    </>
  );
}
